import {
  Array as Arr,
  Chunk,
  Data,
  Effect,
  Option,
  pipe,
  Predicate,
  Schema,
  String as Str,
} from 'effect';

import type { OrderViolationError } from '../../orderViolation';
import { OrderViolationsFromApi } from '../../../../violation/orderViolationSchema';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ConstraintViolationResponse = Schema.Struct({
  violations: Schema.Array(
    Schema.Struct({
      propertyPath: Schema.optional(Schema.String),
      message: Schema.optional(Schema.String),
      code: Schema.optional(Schema.NullOr(Schema.String)),
    })
  ),
});

/**
 * One violation that named the field it is about.
 *
 * A form renders this on the control rather than in a summary, which is the
 * whole reason the field travels separately from the sentence. A violation that
 * named no field is not here; it is in `messages` alone.
 */
export type ViolationField = {
  readonly field: string;
  readonly message: string;
};

/**
 * The violations that named a field, as {@link ViolationField} pairs.
 *
 * A violation with an empty `propertyPath` named no field and is dropped, as is
 * one with no message: a control cannot be marked with nothing.
 */
const violationFields = (
  violations: typeof ConstraintViolationResponse.Type.violations
): ReadonlyArray<ViolationField> =>
  Arr.getSomes(
    violations.map((violation) =>
      Option.flatMap(
        Option.filter(Option.fromNullishOr(violation.propertyPath), Str.isNonEmpty),
        (field) =>
          Option.map(Option.fromNullishOr(violation.message), (message) => ({ field, message }))
      )
    )
  );

const ApiErrorResponse = Schema.Struct({
  detail: Schema.optional(Schema.String),
  title: Schema.optional(Schema.String),
});

/**
 * Shared TypeId for all backend API errors (family pattern)
 */
export const BackendApiErrorTypeId: unique symbol = Symbol.for('@app/BackendApi/Error');
export type BackendApiErrorTypeId = typeof BackendApiErrorTypeId;

/**
 * Factory to create endpoint-specific error classes with type-safe tags
 */
export const createBackendApiError = <
  const Domain extends string,
  const Endpoint extends string,
  const IdentifierField extends string,
>(config: {
  readonly domain: Domain;
  readonly endpoint: Endpoint;
  readonly path: string;
  readonly identifierField: IdentifierField;
}) => {
  const { domain, endpoint, path, identifierField } = config;

  // Create exact literal tag types with @app/BackendApi prefix
  type NotFoundTag = `@app/BackendApi/${Domain}/${Endpoint}/NotFound`;
  type UnauthorizedTag = `@app/BackendApi/${Domain}/${Endpoint}/Unauthorized`;
  type ViolationTag = `@app/BackendApi/${Domain}/${Endpoint}/Violation`;
  type ApiRequestTag = `@app/BackendApi/${Domain}/${Endpoint}/ApiRequest`;

  const notFoundTag = `@app/BackendApi/${domain}/${endpoint}/NotFound` as NotFoundTag;
  const unauthorizedTag = `@app/BackendApi/${domain}/${endpoint}/Unauthorized` as UnauthorizedTag;
  const violationTag = `@app/BackendApi/${domain}/${endpoint}/Violation` as ViolationTag;
  const apiRequestTag = `@app/BackendApi/${domain}/${endpoint}/ApiRequest` as ApiRequestTag;

  // NotFound Error (404)
  class NotFoundError extends Data.TaggedError(notFoundTag)<{
    readonly [key: string]: unknown;
    readonly response: HttpClientResponse.HttpClientResponse;
  }> {
    readonly [BackendApiErrorTypeId]: BackendApiErrorTypeId = BackendApiErrorTypeId;

    get message(): string {
      const id = this[identifierField];
      return `${domain} not found: ${String(id)} (${path})`;
    }

    readonly status = 404 as const;

    get endpoint(): string {
      return path;
    }
  }

  // Unauthorized Error (401)
  class UnauthorizedError extends Data.TaggedError(unauthorizedTag)<{
    readonly [key: string]: unknown;
    readonly response: HttpClientResponse.HttpClientResponse;
  }> {
    readonly [BackendApiErrorTypeId]: BackendApiErrorTypeId = BackendApiErrorTypeId;

    get message(): string {
      const id = this[identifierField];
      return `${domain} unauthorized: ${String(id)} - Authentication required (${path})`;
    }

    readonly status = 401 as const;

    get endpoint(): string {
      return path;
    }
  }

  /**
   * Violation Error (422 with typed violations).
   *
   * `violations` is the coded vocabulary `OrderViolationsFromApi` recognises.
   * `messages` is what the backend wrote, in the order it wrote it, and it is
   * populated even where the coded decode recognises nothing: a violation whose
   * `code` is `null` — every one RacingLine sends — is dropped from
   * `violations` and kept here. A caller that renders the refusal to a shopper
   * reads `messages`; a caller that branches on a known violation reads
   * `violations`.
   *
   * `fields` is the subset that named the field it is about. A cart violation
   * names none — every one RacingLine's cart sends carries `propertyPath: ""` —
   * and a customer violation names one: registering with a taken address
   * answers `propertyPath: "email"`. A form marks the control the shop objected
   * to from this list, and renders the rest of `messages` above the form.
   *
   * `codes` is the third reading of the same list, for a caller that must ACT
   * on the refusal rather than show it. It reads empty on every RacingLine
   * refusal captured so far, for the reason above.
   */
  class ViolationError extends Data.TaggedError(violationTag)<{
    readonly [key: string]: unknown;
    readonly response: HttpClientResponse.HttpClientResponse;
    readonly violations: Chunk.Chunk<OrderViolationError>;
    readonly messages: ReadonlyArray<string>;
    readonly fields: ReadonlyArray<ViolationField>;
  }> {
    readonly [BackendApiErrorTypeId]: BackendApiErrorTypeId = BackendApiErrorTypeId;

    get firstViolation(): Option.Option<OrderViolationError> {
      return Chunk.head(this.violations);
    }

    /**
     * Every code this refusal named, in the order the backend sent them.
     *
     * Read off `violations` rather than stored beside it, so the two cannot
     * disagree. `UnknownViolationError` carries a code the curated vocabulary
     * does not recognise, so a code the backend invented is here too — what is
     * absent is a violation that named no code at all, and it is absent because
     * there is nothing to put here.
     *
     * A caller acting on a refusal reads this. A caller rendering one reads
     * `messages`, which is populated either way.
     */
    get codes(): ReadonlyArray<string> {
      return Chunk.toReadonlyArray(Chunk.map(this.violations, (violation) => violation.code));
    }

    get message(): string {
      const id = this[identifierField];
      return pipe(
        this.firstViolation,
        Option.match({
          onNone: () =>
            `${domain} validation failed: ${String(id)} - No violations found (${path})`,
          onSome: (violation) => {
            const violationMsg = violation.message || violation.code;
            return `${domain} validation failed: ${String(id)} - ${violationMsg} (${path})`;
          },
        })
      );
    }

    readonly status = 422 as const;

    get endpoint(): string {
      return path;
    }
  }

  // Generic API Request Error (all non-404, non-422 failures)
  class ApiRequestError extends Data.TaggedError(apiRequestTag)<{
    readonly [key: string]: unknown;
    readonly response: HttpClientResponse.HttpClientResponse;
    readonly reason?: string;
  }> {
    readonly [BackendApiErrorTypeId]: BackendApiErrorTypeId = BackendApiErrorTypeId;

    get message(): string {
      const id = this[identifierField];
      const status = this.response.status;
      const reasonText = this.reason ? `: ${this.reason}` : '';
      return `${domain} API request failed: ${String(id)} (HTTP ${String(status)})${reasonText} - ${path}`;
    }

    get status(): number {
      return this.response.status;
    }

    get endpoint(): string {
      return path;
    }
  }

  // Factory method for handling 401 responses - always returns UnauthorizedError
  const createUnauthorizedError =
    (identifier: unknown) => (response: HttpClientResponse.HttpClientResponse) => {
      type WithIdentifier = Record<string, unknown>;

      const payload: WithIdentifier & {
        readonly response: HttpClientResponse.HttpClientResponse;
      } = {
        response,
        [identifierField]: identifier,
      };
      return Effect.fail(new UnauthorizedError(payload));
    };

  type ViolationPayload = Record<string, unknown> & {
    readonly response: HttpClientResponse.HttpClientResponse;
    readonly violations: Chunk.Chunk<OrderViolationError>;
    readonly messages: ReadonlyArray<string>;
    readonly fields: ReadonlyArray<ViolationField>;
  };

  /**
   * The 422 body as a constraint-violation document, or `None`.
   *
   * `None` means the body is not one. API Platform answers 422 with a
   * `ConstraintViolation` document for a validation failure, and with its plain
   * error envelope when a command handler refuses — the two carry different
   * facts, and this is the structural difference between them.
   */
  const constraintViolations = (response: HttpClientResponse.HttpClientResponse) =>
    HttpClientResponse.schemaBodyJson(ConstraintViolationResponse)(response).pipe(Effect.option);

  const violationError = (
    identifier: unknown,
    response: HttpClientResponse.HttpClientResponse,
    body: typeof ConstraintViolationResponse.Type
  ) =>
    pipe(
      Schema.decodeEffect(OrderViolationsFromApi)(body.violations),
      Effect.map((violations) => Chunk.fromIterable(violations)),
      // A violation the coded vocabulary does not recognise still has a message.
      Effect.orElseSucceed(() => Chunk.empty<OrderViolationError>()),
      Effect.flatMap((violations) => {
        const payload: ViolationPayload = {
          response,
          violations,
          messages: Arr.getSomes(
            body.violations.map((violation) => Option.fromNullishOr(violation.message))
          ),
          fields: violationFields(body.violations),
          [identifierField]: identifier,
        };
        return Effect.fail(new ViolationError(payload));
      })
    );

  // Factory method for handling 422 responses - always returns ViolationError
  const createViolationError =
    (identifier: unknown) => (response: HttpClientResponse.HttpClientResponse) =>
      constraintViolations(response).pipe(
        Effect.flatMap(
          Option.match({
            onNone: () => {
              const payload: ViolationPayload = {
                response,
                violations: Chunk.empty<OrderViolationError>(),
                messages: [],
                fields: [],
                [identifierField]: identifier,
              };
              return Effect.fail(new ViolationError(payload));
            },
            onSome: (body) => violationError(identifier, response, body),
          })
        )
      );

  /**
   * Factory method for handling 422 responses that may not be validation
   * failures at all.
   *
   * A Sylius cart command answers `422` with
   * `{"detail":"Cart with given token has not been found."}` and no violation
   * list, where a genuine validation failure answers `422` with one. The two
   * mean different things to a page: one refuses what the shopper asked for and
   * leaves the resource intact, the other says the resource is gone. So the
   * body without a violation list becomes `NotFoundError`, which is the error
   * for "there is nothing at this address" whatever status carried it.
   *
   * Use this on a write. `createViolationError` stays the arm for an endpoint
   * whose 422 is always a validation failure.
   */
  const createViolationOrNotFoundError =
    (identifier: unknown) => (response: HttpClientResponse.HttpClientResponse) =>
      constraintViolations(response).pipe(
        Effect.flatMap((decoded): Effect.Effect<never, NotFoundError | ViolationError> =>
          Option.match(decoded, {
            onNone: () => {
              const payload: Record<string, unknown> & {
                readonly response: HttpClientResponse.HttpClientResponse;
              } = { response, [identifierField]: identifier };
              return Effect.fail(new NotFoundError(payload));
            },
            onSome: (body) => violationError(identifier, response, body),
          })
        )
      );

  // Factory method for handling fallback (orElse) responses - decodes problem+json body
  // into ApiRequestError.reason. On decode failure, omits reason.
  const createApiRequestError =
    (identifier: unknown) => (response: HttpClientResponse.HttpClientResponse) => {
      type WithIdentifier = Record<string, unknown>;

      return pipe(
        HttpClientResponse.schemaBodyJson(ApiErrorResponse)(response),
        Effect.map((body) => body.detail ?? body.title ?? null),
        Effect.orElseSucceed(() => null),
        Effect.flatMap((reason) => {
          const base: WithIdentifier & {
            readonly response: HttpClientResponse.HttpClientResponse;
          } = {
            response,
            [identifierField]: identifier,
          };
          const payload = reason !== null ? { ...base, reason } : base;
          return Effect.fail(new ApiRequestError(payload));
        })
      );
    };

  return {
    domain,
    endpoint,
    path,
    identifierField,
    NotFoundError,
    UnauthorizedError,
    ViolationError,
    ApiRequestError,
    createUnauthorizedError,
    createViolationError,
    createViolationOrNotFoundError,
    createApiRequestError,
    Tags: {
      NotFound: notFoundTag,
      Unauthorized: unauthorizedTag,
      Violation: violationTag,
      ApiRequest: apiRequestTag,
    },
  } as const;
};

export type NotFoundError = InstanceType<ReturnType<typeof createBackendApiError>['NotFoundError']>;
export type UnauthorizedError = InstanceType<
  ReturnType<typeof createBackendApiError>['UnauthorizedError']
>;
export type ViolationError = InstanceType<
  ReturnType<typeof createBackendApiError>['ViolationError']
>;
export type ServerError = InstanceType<ReturnType<typeof createBackendApiError>['ApiRequestError']>;

/**
 * Family-level type guard for all backend API errors
 */
export const isBackendApiError = (u: unknown): u is BackendApiError =>
  Predicate.hasProperty(u, BackendApiErrorTypeId);

/**
 * Type guard for 404 NotFound errors
 */
export const isNotFoundError = (u: unknown): u is NotFoundError =>
  isBackendApiError(u) && u.status === 404;

/**
 * Type guard for 401 Unauthorized errors
 */
export const isUnauthorizedError = (u: unknown): u is UnauthorizedError =>
  isBackendApiError(u) && u.status === 401;

/**
 * Type guard for 422 Violation errors
 */
export const isViolationError = (u: unknown): u is ViolationError =>
  isBackendApiError(u) && u.status === 422;

/**
 * Type guard for 409 Conflict errors
 */
export const isConflictError = (u: unknown): u is ServerError =>
  isBackendApiError(u) && u.status === 409;

/**
 * Type guard for 500 Internal Server Error
 */
export const isInternalServerError = (u: unknown): u is ServerError =>
  isBackendApiError(u) && u.status === 500;

/**
 * Union type of all possible backend API errors
 */
export type BackendApiError = NotFoundError | UnauthorizedError | ViolationError | ServerError;

/**
 * Extract error union type from factory result
 */
export type BackendApiErrorUnion<T extends ReturnType<typeof createBackendApiError>> =
  | InstanceType<T['NotFoundError']>
  | InstanceType<T['UnauthorizedError']>
  | InstanceType<T['ViolationError']>
  | InstanceType<T['ApiRequestError']>;

export type { ApiInfrastructureErrors } from '../../api';
